// src/views/admin/users/attendance/component/DataTableFilter.jsx
import React, { useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Input } from '@/components/ui/inputs/input';
import { InputSelect } from '@/components/ui/inputs/input-select';
import ToastMe from '@/components/ui/ToastMe';

export default function DataTableFilter({ onChangeFilter }) {
  const selectRef = useRef(null);
  const { register, setValue, getValues, handleSubmit, control, reset } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone_no: '',
      status: '',
      leaveType: '',
      period: '',
      startDate: '',
      endDate: '',
    },
    mode: 'onSubmit',
  });

  const statusOptions = [
    { value: 'Present', label: 'Present' },
    { value: 'Absent',  label: 'Absent'  },
    { value: 'Leave',   label: 'Leave'   },
    { value: 'Off',     label: 'Off'     },
    { value: 'HalfDay', label: 'HalfDay' },
    { value: 'Other',   label: 'Other'   },
  ];
  const leaveTypeOptions = [
    { value: 'Sick',   label: 'Sick'   },
    { value: 'Casual', label: 'Casual' },
    { value: 'Annual', label: 'Annual' },
    { value: 'Other',  label: 'Other'  },
  ];
  const periodOptions = [
    { value: 'daily',    label: 'Today'         },
    { value: '3days',   label: 'Last 3 Days'    },
    { value: '7days',   label: 'Last 7 Days'    },
    { value: '1month',  label: 'This Month'     },
    { value: '3months', label: 'Last 3 Months'  },
    { value: '6months', label: 'Last 6 Months'  },
    { value: '1year',   label: 'Last 1 Year'    },
  ];

  const convertUndefinedToBlank = (obj) => {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, value === undefined ? '' : value])
    );
  };

  const clearForm = () => {
    try {
      reset();
      onChangeFilter('extra_filter', {});
    } catch (error) {
      const errorMsg = error?.message || 'Error resetting filter';
      ToastMe(errorMsg, 'error');
      console.error('[AttendanceFilter] Reset error:', error);
    }
  };

  const onSubmit = async (data) => {
    try {
      const { period, startDate, endDate, leaveType, ...rest } = data;
      
      // Build extra_filter object
      const filterData = { ...rest };
      
      // Add date filters
      if (period) {
        filterData.period = period;
      } else if (startDate && endDate) {
        filterData.startDate = startDate;
        filterData.endDate = endDate;
      } else if (startDate) {
        filterData.startDate = startDate;
      } else if (endDate) {
        filterData.endDate = endDate;
      }
      
      // Remove empty values
      const cleaned = Object.fromEntries(
        Object.entries(filterData).filter(([k, v]) => v !== '' && v != null)
      );
      
      const finalFilter = convertUndefinedToBlank(cleaned);
      await onChangeFilter('extra_filter', finalFilter);
    } catch (error) {
      const errorMsg = error?.message || 'Error applying filter';
      ToastMe(errorMsg, 'error');
      console.error('[AttendanceFilter] Error:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-5 mb-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">

        {/* Employee Name */}
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input {...field}
                   labelText="Employee Name"
                   size="sm"
                   className="w-full" />
          )}
        />

        {/* Email */}
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input {...field}
                   labelText="Email"
                   type="email"
                   size="sm"
                   className="w-full" />
          )}
        />

        {/* Phone */}
        <Controller
          name="phone_no"
          control={control}
          render={({ field }) => (
            <Input {...field}
                   labelText="Phone"
                   size="sm"
                   className="w-full" />
          )}
        />

        {/* Status */}
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <InputSelect {...field}
                         ref={selectRef}
                         labelText="Status"
                         size="sm"
                         options={statusOptions}
                         className="w-full"
                         onChange={opt => setValue('status', opt?.value || '')}
                         value={statusOptions.find(o => o.value === getValues('status'))}
            />
          )}
        />

        {/* Leave Type */}
        <Controller
          name="leaveType"
          control={control}
          render={({ field }) => (
            <InputSelect {...field}
                         ref={selectRef}
                         labelText="Leave Type"
                         size="sm"
                         options={leaveTypeOptions}
                         className="w-full"
                         onChange={opt => setValue('leaveType', opt?.value || '')}
                         value={leaveTypeOptions.find(o => o.value === getValues('leaveType'))}
            />
          )}
        />

        {/* Period */}
        <Controller
          name="period"
          control={control}
          render={({ field }) => (
            <InputSelect {...field}
                         ref={selectRef}
                         labelText="Period"
                         size="sm"
                         options={periodOptions}
                         className="w-full"
                         onChange={opt => {
                           setValue('period', opt?.value || '');
                           setValue('startDate', '');
                           setValue('endDate', '');
                         }}
                         value={periodOptions.find(o => o.value === getValues('period'))}
            />
          )}
        />

        {/* Start Date */}
        <Input
          type="date"
          labelText="Start Date"
          size="sm"
          className="w-full"
          {...register('startDate')}
          onChange={e => {
            setValue('startDate', e.target.value);
            setValue('period', '');
          }}
        />

        {/* End Date */}
        <Input
          type="date"
          labelText="End Date"
          size="sm"
          className="w-full"
          {...register('endDate')}
          onChange={e => {
            setValue('endDate', e.target.value);
            setValue('period', '');
          }}
        />

      </div>

      {/* Buttons */}
      <div className="flex gap-3 mt-4">
        <button type="submit" className="db-btn bg-primary text-white py-2 px-4">
          <i className="lab lab-search-line mr-2"></i> Search
        </button>
        <button type="button" onClick={clearForm}
                className="db-btn bg-gray-600 text-white py-2 px-4">
          <i className="lab lab-cross-line-2 mr-2"></i> Clear
        </button>
      </div>
    </form>
  );
}
